import { ReadonlyVec3, vec3 } from 'gl-matrix';
import type { Grid } from '~/engine/data/common/Grid';
import { DirectionLabel, directionLabel } from '~/utils/orientation';

/**
 * GL-free rules of the adjacent-slice contour ghosts (MIM parity §5.9.1):
 * which slices each side of the displayed one shows, in which line pattern
 * and at what strength, and where those slices lie. A display aid only;
 * free of WebGL so it is unit tested without a GL context.
 */

/** A screen-space line pattern: `onPx` drawn at the start of every `periodPx` along the line. */
type DashPattern = { readonly onPx: number; readonly periodPx: number };

/** The two sides of the displayed slice, named by their ghosts' line pattern ({@link ghostSideTowards}). */
export const GHOST_SIDES = ['dashed', 'dotted'] as const;

export type GhostSide = (typeof GHOST_SIDES)[number];

/** The line pattern of the ghosts on each side. */
export const GHOST_PATTERNS: Readonly<Record<GhostSide, DashPattern>> = {
    dashed: { onPx: 8, periodPx: 12 },
    dotted: { onPx: 2, periodPx: 5 },
};

/**
 * The anatomical directions, by the letters the viewport's edges carry, that
 * each side's pattern stands for: in a transversal, coronal and sagittal view
 * in turn. The dashed side is the positive end of each DICOM LPS axis.
 */
export const GHOST_SIDE_DIRECTIONS: Readonly<
    Record<GhostSide, ReadonlyArray<DirectionLabel>>
> = { dashed: ['H', 'P', 'L'], dotted: ['F', 'A', 'R'] };

/** Every depth a slice can be ghosted at, nearest first; 1 is the neighbouring slice. */
export const GHOST_DEPTHS = [1, 2, 3] as const;

/** How many scroll steps away a ghosted slice lies. */
export type GhostDepth = (typeof GHOST_DEPTHS)[number];

/** Which slices are ghosted on each side of the displayed one, by how many scroll steps away they lie. */
export type AdjacentSliceGhostDepths = Readonly<
    Record<GhostSide, ReadonlyArray<GhostDepth>>
>;

/** The neighbouring slice on each side, and no more. */
export const DEFAULT_ADJACENT_SLICE_GHOST_DEPTHS: AdjacentSliceGhostDepths = {
    dashed: [1],
    dotted: [1],
};

/**
 * Opacity of a ghost by how far away its slice lies: the neighbour at full
 * strength, each slice beyond it fainter so several on one side read in
 * order, and none below one half so the faintest is still plainly drawn.
 */
export const GHOST_DEPTH_ALPHA: Readonly<Record<GhostDepth, number>> = {
    1: 1,
    2: 0.75,
    3: 0.55,
};

/**
 * Displacement, in patient-space mm, from the displayed plane to the one
 * `depth` wheel ticks along `direction` display on `grid`. Each step is the
 * scroll action's ({@link Grid.scrollStep}), taken from where the one before
 * landed, so from a position between slice centres each side still shows
 * the slice its ticks reach rather than a blend. Only the through-plane part
 * of a step counts: the plane keeps its in-plane placement.
 *
 * @param position A point on the displayed plane, in patient space.
 * @param direction The view normal or its negation; only its direction is used.
 */
export function neighbouringSliceOffsetMm(
    grid: Pick<Grid, 'scrollStep'>,
    position: ReadonlyVec3,
    direction: ReadonlyVec3,
    depth: GhostDepth = 1
): vec3 {
    const unit = vec3.normalize(vec3.create(), direction);
    const at = vec3.clone(position);
    let depthMm = 0;
    for (let taken = 0; taken < depth; taken += 1) {
        const step = grid.scrollStep(at, unit);
        depthMm += vec3.dot(step, unit);
        vec3.add(at, at, step);
    }
    return vec3.scale(unit, unit, depthMm);
}

/**
 * The side the slices lying in `patientDir` from the displayed plane belong
 * to, by the anatomical direction it is most aligned with
 * ({@link GHOST_SIDE_DIRECTIONS}). Tied to anatomy rather than scroll order,
 * the pattern stays the same however the image was acquired or the view rotated.
 */
export function ghostSideTowards(patientDir: ReadonlyVec3): GhostSide {
    return GHOST_SIDE_DIRECTIONS.dashed.includes(directionLabel(patientDir))
        ? 'dashed'
        : 'dotted';
}
